import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/DBContext';
import { GetCustomClaimRole } from '../api/stripe';

const ProtectedRoute = ({ allowedRoles = [] }) => {
    const { currentUser, userInfo } = useAuth();
    const [stripeRole, setStripeRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const location = useLocation();

    useEffect(() => {
        const fetchStripeRole = async () => {
            if (currentUser) {
                try {
                    const role = await GetCustomClaimRole();
                    setStripeRole(role);
                } catch (error) {
                    console.error('Error fetching stripe role:', error);
                    setStripeRole('inactive');
                }
            }
            setLoading(false);
        };

        fetchStripeRole();
    }, [currentUser]);

    // Don't make any routing decisions until we have both userInfo and stripeRole loaded
    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    if (loading || !userInfo) {
        return null; // or a loading spinner
    }

    // Admin has access to everything
    if (userInfo?.role === 'admin') {
        return <Outlet />;
    }

    // If user is business or local-authority and not approved
    if (
        (userInfo.role === 'business' || userInfo.role === 'local-authority') &&
        !userInfo.approved
    ) {
        return <Navigate to="/approval-pending" />;
    }

    // Check if user's role is allowed for this route
    if (allowedRoles.length > 0 && !allowedRoles.includes(userInfo.role)) {
        // Store the attempted path to redirect back after role is confirmed
        return <Navigate to="/dashboard" state={{ from: location.pathname }} />;
    }

    // For family and business accounts, check subscription status
    if (['family', 'business'].includes(userInfo.role)) {
        // Skip subscription check for auth-pricing page to avoid infinite redirect
        if (location.pathname !== '/auth-pricing') {
            // If user is in test mode, skip subscription check
            if (userInfo.is_test) {
                return <Outlet />;
            }
            
            // If inactive or role mismatch, redirect to auth pricing page
            if (stripeRole === 'inactive' || stripeRole !== userInfo.role) {
                return <Navigate to="/auth-pricing" />;
            }
        }
    }

    return <Outlet />;
};

export default ProtectedRoute; 